import { useCallback, useMemo } from "react";
import {
  buildEmptyBilledQtyMap,
  buildFullBilledQtyMap,
  getSelectableItemIds,
  OrderItemQuantitySource,
} from "../Utils/orderItemQuantity";

type SetFieldValue = (field: string, value: unknown) => void;

type UseInvoiceItemSelectionArgs = {
  orderItems: OrderItemQuantitySource[];
  selectedRowId: string[];
  setSelectedRowId: React.Dispatch<React.SetStateAction<string[]>>;
  setBilledQuantities: React.Dispatch<React.SetStateAction<Record<string, any>>>;
};

/**
 * Syncs Select All / per-row selection with Formik `billed_qty` and local billedQuantities.
 * Select All fills remaining qty; per-row select does not auto-fill.
 */
export const useInvoiceItemSelection = ({
  orderItems,
  selectedRowId,
  setSelectedRowId,
  setBilledQuantities,
}: UseInvoiceItemSelectionArgs) => {
  // Keep raw item.id values so selection matches per-row Checkbox checks.
  const selectableIds = useMemo(
    () => getSelectableItemIds(orderItems) as string[],
    [orderItems]
  );

  const allSelected =
    selectableIds.length > 0 &&
    selectableIds.every((id) => selectedRowId.includes(id));

  const isIndeterminate = selectedRowId.length > 0 && !allSelected;
  const isSelectAllDisabled = selectableIds.length === 0;

  const handleSelectAll = useCallback(
    (checked: boolean, setFieldValue: SetFieldValue) => {
      if (checked) {
        const billedQtyMap = buildFullBilledQtyMap(orderItems);
        setSelectedRowId(selectableIds);
        setFieldValue("billed_qty", billedQtyMap);
        setBilledQuantities(billedQtyMap);
        return;
      }

      const emptyMap = buildEmptyBilledQtyMap(orderItems);
      setSelectedRowId([]);
      setFieldValue("billed_qty", emptyMap);
      setBilledQuantities({});
    },
    [orderItems, selectableIds, setSelectedRowId, setBilledQuantities]
  );

  const handleRowToggle = useCallback(
    (id: string, setFieldValue: SetFieldValue) => {
      const isSelected = selectedRowId.includes(id);

      if (isSelected) {
        setSelectedRowId((prev) => prev.filter((rowId) => rowId !== id));
        setFieldValue(`billed_qty.${id}`, "");
        setBilledQuantities((prev) => ({
          ...prev,
          [id]: "",
        }));
        return;
      }

      // Select id only — do not auto-fill process quantity
      setSelectedRowId((prev) => [...prev, id]);
    },
    [selectedRowId, setSelectedRowId, setBilledQuantities]
  );

  return {
    selectableIds,
    allSelected,
    isIndeterminate,
    isSelectAllDisabled,
    handleSelectAll,
    handleRowToggle,
  };
};
